import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../auth";
import { getPlant, updatePlant } from "../db/plants";
import { createAttribute } from "../db/plantAttributes";
import { createTask } from "../db/tasks";
import { MarkdownEditor } from "../components/MarkdownEditor";
import type { PlantRow, TaskCategory } from "@garden/types/db";
import "./CareNotes.css";

const TASK_CATEGORIES: { value: TaskCategory; label: string }[] = [
  { value: "care", label: "Care" },
  { value: "growing", label: "Growing" },
  { value: "project", label: "Project" },
  { value: "purchase", label: "Purchase" },
  { value: "maintenance", label: "Maintenance" },
];

interface AttrDraft {
  id: string;
  key: string;
  value: string;
}

interface TaskDraft {
  id: string;
  name: string;
  category: TaskCategory;
  due_date: string;
  description: string;
}

const newId = () => Math.random().toString(36).slice(2, 10);

const blankAttr = (): AttrDraft => ({ id: newId(), key: "", value: "" });
const blankTask = (): TaskDraft => ({
  id: newId(),
  name: "",
  category: "care",
  due_date: "",
  description: "",
});

const AUTO_SAVE_INTERVAL_MS = 10_000;

export function CareNotes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [plant, setPlant] = useState<PlantRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [savingNotes, setSavingNotes] = useState(false);
  const [attrs, setAttrs] = useState<AttrDraft[]>([blankAttr()]);
  const [tasks, setTasks] = useState<TaskDraft[]>([]);
  const [working, setWorking] = useState(false);

  // Load plant.
  useEffect(() => {
    if (!supabase || !id) return;
    void (async () => {
      try {
        const p = await getPlant(supabase!, id);
        setPlant(p);
        setNotes(p.notes ?? "");
        setSavedNotes(p.notes ?? "");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [id]);

  // Tick the "Auto-saved Xs ago" label.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-save the prose every 10 seconds while it's dirty. Per ADR-0023,
  // the attribute / task drafts on the right pane are NOT auto-saved —
  // they're committed in one transaction when the user hits "Save".
  // Local-state-only; if the tab is closed mid-edit, the last <10s of
  // typing is lost. Acceptable for v1 per the plan's open question.
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const savedNotesRef = useRef(savedNotes);
  savedNotesRef.current = savedNotes;

  useEffect(() => {
    if (!supabase || !id) return;
    const handle = setInterval(async () => {
      const current = notesRef.current;
      if (current === savedNotesRef.current) return;
      try {
        setSavingNotes(true);
        await updatePlant(supabase!, id, { notes: current });
        setSavedNotes(current);
        setSavedAt(Date.now());
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setSavingNotes(false);
      }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [id]);

  const dirty = notes !== savedNotes;
  const savedLabel = useMemo(() => {
    if (savingNotes) return "Saving…";
    if (dirty) return "Editing — auto-save in <10 s";
    if (savedAt === null) return "Loaded · no changes yet";
    const ago = Math.max(0, Math.round((now - savedAt) / 1000));
    if (ago < 5) return "Auto-saved · just now";
    if (ago < 60) return `Auto-saved · ${ago}s ago`;
    return `Auto-saved · ${Math.floor(ago / 60)}m ago`;
  }, [savingNotes, dirty, savedAt, now]);

  const attrsToCommit = useMemo(
    () => attrs.filter((a) => a.key.trim() !== ""),
    [attrs],
  );
  const tasksToCommit = useMemo(
    () => tasks.filter((t) => t.name.trim() !== ""),
    [tasks],
  );

  const onAttrChange = (id: string, patch: Partial<AttrDraft>) => {
    setAttrs((cur) => cur.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const onAttrAdd = () => setAttrs((cur) => [...cur, blankAttr()]);
  const onAttrRemove = (id: string) =>
    setAttrs((cur) => cur.filter((a) => a.id !== id));

  const onTaskChange = (id: string, patch: Partial<TaskDraft>) => {
    setTasks((cur) => cur.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };
  const onTaskAdd = () => setTasks((cur) => [...cur, blankTask()]);
  const onTaskRemove = (id: string) =>
    setTasks((cur) => cur.filter((t) => t.id !== id));

  const onSaveAll = async () => {
    if (!supabase || !id || !plant) return;
    setWorking(true);
    setError(null);
    try {
      // Commit prose if dirty.
      if (dirty) {
        await updatePlant(supabase, id, { notes });
        setSavedNotes(notes);
        setSavedAt(Date.now());
      }

      // Attributes — fire in parallel.
      await Promise.all(
        attrsToCommit.map((a) =>
          createAttribute(supabase!, {
            plant_id: id,
            key: a.key.trim(),
            value: a.value.trim() === "" ? null : a.value.trim(),
          }),
        ),
      );

      // Tasks.
      await Promise.all(
        tasksToCommit.map((t) =>
          createTask(supabase!, {
            name: t.name.trim(),
            category: t.category,
            plant_id: id,
            due_date: t.due_date === "" ? null : t.due_date,
            description: t.description.trim() === "" ? null : t.description.trim(),
          }),
        ),
      );

      navigate(`/plants/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setWorking(false);
    }
  };

  if (!plant && !error) {
    return <div className="CareNotes__loading">Loading…</div>;
  }
  if (!plant) {
    return <div className="CareNotes__error">{error ?? "Plant not found."}</div>;
  }

  const plantTitle =
    plant.cultivar ? `${plant.common_name} '${plant.cultivar}'` : plant.common_name;

  const saveLabel = (() => {
    const a = attrsToCommit.length;
    const t = tasksToCommit.length;
    if (a === 0 && t === 0) return "Save notes";
    const extras: string[] = [];
    if (a > 0) extras.push(`${a} attribute${a === 1 ? "" : "s"}`);
    if (t > 0) extras.push(`${t} task${t === 1 ? "" : "s"}`);
    return `Save notes & create ${extras.join(" + ")}`;
  })();

  return (
    <div className="CareNotes">
      <div className="CareNotes__bread">
        <Link to="/plants">Plants</Link>
        <span className="CareNotes__sep">›</span>
        <Link to={`/plants/${plant.id}`}>{plantTitle}</Link>
        <span className="CareNotes__sep">›</span>
        <em>Add care notes</em>
      </div>
      <div className="CareNotes__head">
        <h1>
          Care notes <em>· {plantTitle}</em>
        </h1>
        <p>
          Paste prose from RHS, a book, a friend's email — anywhere.
          Optionally pull a few key facts into structured attributes.
          The notes alone are searchable; structured fields are useful
          only if you want to filter on them.
        </p>
      </div>

      <div className="CareNotes__grid">
        {/* LEFT: paste */}
        <div className="CareNotes__col">
          <div className="CareNotes__colH">01 · The prose</div>

          <label className="CareNotes__source">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M5 9l-2 2a2 2 0 0 1-2.8-2.8L4 4.5M9 5l2-2a2 2 0 0 1 2.8 2.8L9.5 10" />
            </svg>
            <input
              type="url"
              placeholder="Source URL (optional) — e.g. rhs.org.uk/plants/…"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </label>

          <MarkdownEditor
            value={notes}
            onChange={setNotes}
            startInPreview={false}
            placeholder="Paste prose here. Use # / ## for headings."
            minHeight={360}
          />
        </div>

        {/* RIGHT: structured */}
        <div className="CareNotes__col">
          <div className="CareNotes__colH">02 · Structured (optional)</div>

          <div className="CareNotes__card">
            <div className="CareNotes__cardH">
              <div className="CareNotes__cardTitle">Attributes</div>
              <div className="CareNotes__cardMeta">
                {attrsToCommit.length} captured
              </div>
            </div>
            {attrs.map((a) => (
              <div className="CareNotes__attrRow" key={a.id}>
                <input
                  type="text"
                  className="CareNotes__attrKey"
                  placeholder="Sun, Soil, Hardiness…"
                  value={a.key}
                  onChange={(e) => onAttrChange(a.id, { key: e.target.value })}
                />
                <input
                  type="text"
                  className="CareNotes__attrValue"
                  placeholder="Full / part shade"
                  value={a.value}
                  onChange={(e) => onAttrChange(a.id, { value: e.target.value })}
                />
                <button
                  type="button"
                  className="CareNotes__attrDel"
                  aria-label="Remove attribute"
                  onClick={() => onAttrRemove(a.id)}
                  disabled={attrs.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="CareNotes__addRow"
              onClick={onAttrAdd}
            >
              + Add attribute
            </button>
          </div>

          <div className="CareNotes__card">
            <div className="CareNotes__cardH">
              <div className="CareNotes__cardTitle">Tasks to add</div>
              <div className="CareNotes__cardMeta">
                {tasksToCommit.length} draft{tasksToCommit.length === 1 ? "" : "s"}
              </div>
            </div>
            {tasks.length === 0 && (
              <p className="CareNotes__empty">
                Add any recurring jobs the source mentions — pruning,
                feeding, mulching. Each becomes a task on this plant.
              </p>
            )}
            {tasks.map((t) => (
              <div className="CareNotes__taskRow" key={t.id}>
                <input
                  type="text"
                  className="CareNotes__taskName"
                  placeholder="e.g. Hard prune"
                  value={t.name}
                  onChange={(e) => onTaskChange(t.id, { name: e.target.value })}
                />
                <select
                  className="CareNotes__taskCat"
                  value={t.category}
                  onChange={(e) =>
                    onTaskChange(t.id, { category: e.target.value as TaskCategory })
                  }
                >
                  {TASK_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  className="CareNotes__taskDate"
                  value={t.due_date}
                  onChange={(e) => onTaskChange(t.id, { due_date: e.target.value })}
                />
                <button
                  type="button"
                  className="CareNotes__attrDel"
                  aria-label="Remove task"
                  onClick={() => onTaskRemove(t.id)}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="CareNotes__addRow"
              onClick={onTaskAdd}
            >
              + Add task
            </button>
          </div>

          <p className="CareNotes__future">
            In v2.2 the Claude API will extract attributes and tasks for
            you. In v1, this is a hand-tidy step — and it's optional.
          </p>
        </div>
      </div>

      {error && <div className="CareNotes__errorBanner">{error}</div>}

      <div className="CareNotes__foot">
        <div className="CareNotes__footMeta">{savedLabel}</div>
        <div className="CareNotes__footActions">
          <Link
            to={`/plants/${plant.id}`}
            className="CareNotes__btn CareNotes__btn--secondary"
          >
            Cancel
          </Link>
          <button
            type="button"
            className="CareNotes__btn CareNotes__btn--primary"
            onClick={onSaveAll}
            disabled={working}
          >
            {working ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
